import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Customer } from '../../customer/customer.model';
import { Driver } from '../../fleet/driver/driver.model';
import { DriverService } from '../../fleet/driver/driver.service';
import { PartyService } from '../../party/party.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_ACTION_STATUSES,
  HR_DOCUMENT_CONFIDENTIALITY_LEVELS,
  HR_EMPLOYEE_STATUSES,
  HR_TASK_STATUSES,
  HrActivationReadiness,
  HrDepartment,
  HrEmployeeActionInput,
  HrDocumentType,
  HrEmployee,
  HrEmployeeDocument,
  HrOffboardingPlan,
  HrOnboardingPlan,
  HrOnboardingTemplate,
  HrPosition
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-employee-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    BadgeModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TabsModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-employee-detail.component.html',
  styleUrls: ['./hr-employee-detail.component.scss']
})
export class HrEmployeeDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly hrService = inject(HrService);
  private readonly driverService = inject(DriverService);
  private readonly partyService = inject(PartyService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly tabKeys = ['overview', 'employment', 'documents', 'org', 'links', 'lifecycle', 'timeline'] as const;

  employeeId = Number(this.route.snapshot.paramMap.get('id'));
  activeTab: 'overview' | 'employment' | 'documents' | 'org' | 'links' | 'lifecycle' | 'timeline' = 'overview';
  employee?: HrEmployee;
  loading = false;
  statusValue = '';
  statusReason = '';
  customerId?: number;
  driverId?: number;
  documentTypes: HrDocumentType[] = [];
  employeeDocuments: HrEmployeeDocument[] = [];
  onboardingTemplates: HrOnboardingTemplate[] = [];
  onboardingPlans: HrOnboardingPlan[] = [];
  offboardingPlans: HrOffboardingPlan[] = [];
  activationReadiness?: HrActivationReadiness;
  documentForm: HrEmployeeDocument = this.emptyDocumentForm();
  onboardingRequest: { templateId?: number; targetCompletionDate?: string; notes?: string } = {};
  actionForm: HrEmployeeActionInput = {};
  inviteForm = { corporateRole: 'EMPLOYEE' };
  inviteRoleIdsText = '';
  selectedDocument?: HrEmployeeDocument;
  verificationDialogVisible = false;
  verificationTargetStatus = 'VERIFIED';
  verificationReason = '';
  statusDialogVisible = false;
  documentDialogVisible = false;
  customerLinkDialogVisible = false;
  driverLinkDialogVisible = false;
  lifecycleActionDialogVisible = false;
  onboardingDialogVisible = false;
  invitationDialogVisible = false;
  customers: Customer[] = [];
  drivers: Driver[] = [];
  departments: HrDepartment[] = [];
  positions: HrPosition[] = [];

  readonly statusOptions = HR_EMPLOYEE_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly taskStatusOptions = HR_TASK_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly actionStatusOptions = HR_ACTION_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly confidentialityOptions = HR_DOCUMENT_CONFIDENTIALITY_LEVELS.map(value => ({ label: this.format(value), value }));
  readonly canEditEmployee = this.permissionService.hasPermission(app_permissions.hr.employees.edit);
  readonly canChangeStatus = this.permissionService.hasPermission(app_permissions.hr.employees.status);
  readonly canLinkIdentities = this.permissionService.hasPermission(app_permissions.hr.employees.link);
  readonly canManageDocuments = this.permissionService.hasPermission(app_permissions.hr.documents.manage);
  readonly canVerifyDocuments = this.permissionService.hasPermission(app_permissions.hr.documents.verify);
  readonly canViewRestrictedDocuments = this.permissionService.hasPermission(app_permissions.hr.documents.restrictedView);
  readonly canManageLifecycle = this.permissionService.hasPermission(app_permissions.hr.lifecycle.manage);
  readonly canManageOnboarding = this.permissionService.hasPermission(app_permissions.hr.onboarding.manage);
  readonly canManageOffboarding = this.permissionService.hasPermission(app_permissions.hr.offboarding.manage);

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.activeTab = this.resolveTab(params.get('tab'));
    });
    this.loadCustomers();
    this.loadDrivers();
    this.loadDepartments();
    this.loadPositions();
    this.load();
  }

  setActiveTab(value: string | number): void {
    const resolvedTab = this.resolveTab(String(value));
    this.activeTab = resolvedTab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: resolvedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Status', value: this.format(this.employee?.status), tone: this.employee?.status === 'ACTIVE' ? 'success' : 'info' },
      { label: 'Category', value: this.format(this.employee?.workerCategory) },
      { label: 'Portal Link', value: this.employee?.customerLinked ? 'Linked' : 'Missing', tone: this.employee?.customerLinked ? 'success' : 'warn' },
      { label: 'Driver Link', value: this.employee?.driverLinked ? 'Linked' : 'Missing', tone: this.employee?.driverLinked ? 'success' : 'warn' },
      { label: 'Documents', value: this.employeeDocuments.length, tone: 'info' },
      { label: 'Expired Docs', value: this.countDocuments('EXPIRED'), tone: this.countDocuments('EXPIRED') > 0 ? 'danger' : 'success' },
      { label: 'Onboarding Plans', value: this.onboardingPlans.length, tone: 'info' },
      { label: 'Activation Gates', value: this.activationReadiness?.ready ? 'Ready' : `${this.activationReadiness?.blockerCount ?? 0} blockers`, tone: this.activationReadiness?.ready ? 'success' : 'warn' },
      { label: 'Offboarding Plans', value: this.offboardingPlans.length, tone: this.offboardingPlans.length ? 'warn' : 'success' },
      { label: 'Events', value: this.employee?.lifecycleEvents?.length ?? 0 },
      { label: 'Employment Records', value: this.employee?.employmentRecords?.length ?? 0 }
    ];
  }

  get documentTypeOptions(): { label: string; value?: number }[] {
    return this.documentTypes.map(type => ({
      label: `${type.documentName || type.documentCode} (${type.documentCode})`,
      value: type.documentTypeId
    }));
  }

  get onboardingTemplateOptions(): { label: string; value?: number }[] {
    return this.onboardingTemplates.map(template => ({
      label: `${template.templateName || template.templateCode} (${this.format(template.workerCategory)})`,
      value: template.templateId
    }));
  }

  get customerOptions() {
    return this.customers
      .sort((left, right) => (left.name || '').localeCompare(right.name || ''))
      .map(customer => ({
        label: this.customerLabel(customer),
        value: customer.id,
        emailAddress: customer.emailAddress || '',
        phoneNumber: customer.phoneNumber || ''
      }));
  }

  get driverOptions(): { label: string; value: number | undefined }[] {
    return this.drivers.map(driver => ({
      label: this.driverLabel(driver),
      value: driver.id
    }));
  }

  get departmentOptions(): { label: string; value: number | undefined }[] {
    return this.departments.map(department => ({
      label: `${department.departmentName || department.departmentCode || 'Department'} (${department.departmentCode || department.departmentId || '-'})`,
      value: department.departmentId
    }));
  }

  get positionOptions(): { label: string; value: number | undefined }[] {
    return this.positions.map(position => ({
      label: `${position.positionName || position.positionCode || 'Position'} (${position.positionCode || position.positionId || '-'})${position.departmentName ? ' - ' + position.departmentName : ''}`,
      value: position.positionId
    }));
  }

  load(): void {
    this.loading = true;
    this.hrService.employee(this.employeeId).subscribe({
      next: response => {
        this.employee = response.data ?? undefined;
        this.statusValue = this.employee?.status || '';
        this.loadDocumentTypes();
        this.loadDocuments();
        this.loadLifecycleWork();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load employee.' }),
      complete: () => this.loading = false
    });
  }

  loadDocumentTypes(): void {
    this.hrService.documentTypes().subscribe({
      next: response => this.documentTypes = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to load document types.' })
    });
  }

  loadCustomers(): void {
    this.partyService.getParties(250, ['Employee'], '', 0, 1000).subscribe({
      next: response => this.customers = (response.content || []).map(party => this.partyService.convertPartyToCustomer(party)),
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load portal employee dropdown.' })
    });
  }

  loadDrivers(): void {
    this.driverService.getFreelanceDriversByCompanyId(0, 500, 250).subscribe({
      next: response => this.drivers = response.items || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load driver dropdown.' })
    });
  }

  loadDepartments(): void {
    this.hrService.departments(250).subscribe({
      next: response => this.departments = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load department dropdown.' })
    });
  }

  loadPositions(): void {
    this.hrService.positions(250).subscribe({
      next: response => this.positions = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load position dropdown.' })
    });
  }

  loadDocuments(): void {
    this.hrService.employeeDocuments(this.employeeId).subscribe({
      next: response => this.employeeDocuments = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to load employee documents.' })
    });
  }

  loadLifecycleWork(): void {
    this.hrService.onboardingTemplates().subscribe({
      next: response => this.onboardingTemplates = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Onboarding', detail: 'Unable to load onboarding templates.' })
    });
    this.hrService.employeeOnboarding(this.employeeId).subscribe({
      next: response => this.onboardingPlans = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Onboarding', detail: 'Unable to load onboarding plans.' })
    });
    this.hrService.employeeOffboarding(this.employeeId).subscribe({
      next: response => this.offboardingPlans = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Offboarding', detail: 'Unable to load offboarding plans.' })
    });
    this.loadActivationReadiness();
  }

  loadActivationReadiness(): void {
    this.hrService.activationReadiness(this.employeeId).subscribe({
      next: response => this.activationReadiness = response.data || undefined,
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to evaluate activation gates.' })
    });
  }

  saveDocument(): void {
    if (!this.canManageDocuments) return;
    this.hrService.saveEmployeeDocument(this.employeeId, this.documentForm).subscribe({
      next: () => {
        this.documentDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'HR Documents', detail: 'Document saved.' });
        this.resetDocumentForm();
        this.loadDocuments();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to save document.' })
    });
  }

  editDocument(document: HrEmployeeDocument): void {
    if (!this.canManageDocuments) return;
    this.documentForm = { ...document };
    this.documentDialogVisible = true;
  }

  openDocumentDialog(): void {
    if (!this.canManageDocuments) return;
    this.resetDocumentForm();
    this.documentDialogVisible = true;
  }

  resetDocumentForm(): void {
    this.documentForm = this.emptyDocumentForm();
  }

  emptyDocumentForm(): HrEmployeeDocument {
    return {
      employeeId: this.employeeId,
      verificationStatus: 'PENDING',
      confidentialityLevel: 'INTERNAL',
      active: true
    };
  }

  copyDriverLegalDocuments(): void {
    if (!this.canManageDocuments) return;
    this.hrService.copyDriverLegalDocuments(this.employeeId).subscribe({
      next: response => {
        this.employeeDocuments = response.data || this.employeeDocuments;
        this.messageService.add({ severity: 'success', summary: 'HR Documents', detail: `${response.data?.length || 0} driver documents copied.` });
        this.loadDocuments();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to copy driver legal documents.' })
    });
  }

  openVerification(document: HrEmployeeDocument, status: string): void {
    if (!this.canVerifyDocuments) return;
    this.selectedDocument = document;
    this.verificationTargetStatus = status;
    this.verificationReason = '';
    this.verificationDialogVisible = true;
  }

  confirmVerification(): void {
    if (!this.canVerifyDocuments) return;
    if (!this.selectedDocument?.documentId) return;
    const request = this.verificationTargetStatus === 'REJECTED'
      ? this.hrService.rejectDocument(this.selectedDocument.documentId, this.verificationReason)
      : this.verificationTargetStatus === 'WAIVED'
        ? this.hrService.waiveDocument(this.selectedDocument.documentId, this.verificationReason)
        : this.hrService.verifyDocument(this.selectedDocument.documentId, this.verificationReason);
    request.subscribe({
      next: () => {
        this.verificationDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'HR Documents', detail: `Document ${this.format(this.verificationTargetStatus).toLowerCase()}.` });
        this.loadDocuments();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to update document verification.' })
    });
  }

  countDocuments(status: string): number {
    return this.employeeDocuments.filter(document => document.complianceStatus === status).length;
  }

  canOpenDocumentFile(document: HrEmployeeDocument): boolean {
    return !!document.fileUrl && (!this.isRestrictedDocument(document) || this.canViewRestrictedDocuments);
  }

  previewDocument(document: HrEmployeeDocument): void {
    if (!this.canOpenDocumentFile(document) || !document.fileUrl) return;
    window.open(document.fileUrl, '_blank', 'noopener,noreferrer');
  }

  downloadDocument(document: HrEmployeeDocument): void {
    if (!this.canOpenDocumentFile(document) || !document.fileUrl) return;
    const anchor = window.document.createElement('a');
    anchor.href = document.fileUrl;
    anchor.download = document.fileName || `${document.documentCode || 'hr-document'}`;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  }

  documentSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'VALID' || status === 'WAIVED') return 'success';
    if (status === 'EXPIRING_SOON' || status === 'PENDING_VERIFICATION') return 'warn';
    if (status === 'EXPIRED' || status === 'MISSING' || status === 'REJECTED') return 'danger';
    return 'secondary';
  }

  startOnboarding(): void {
    if (!this.canManageOnboarding) return;
    this.hrService.startOnboarding(this.employeeId, this.onboardingRequest).subscribe({
      next: () => {
        this.onboardingDialogVisible = false;
        this.onboardingRequest = {};
        this.messageService.add({ severity: 'success', summary: 'HR Onboarding', detail: 'Onboarding started.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Onboarding', detail: 'Unable to start onboarding.' })
    });
  }

  completeOnboardingTask(taskId?: number, taskStatus = 'COMPLETED'): void {
    if (!this.canManageOnboarding || !taskId) return;
    this.hrService.completeOnboardingTask(taskId, { taskStatus }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Onboarding', detail: 'Onboarding task updated.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Onboarding', detail: 'Unable to update onboarding task.' })
    });
  }

  activateEmployee(): void {
    if (!this.canManageOnboarding) return;
    this.hrService.activateEmployee(this.employeeId, { reason: 'Activated from HR lifecycle workspace.' }).subscribe({
      next: response => {
        this.employee = response.data ?? this.employee;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Employee activated.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Activation gates are not satisfied.' })
    });
  }

  inviteEmployee(): void {
    if (!this.canManageLifecycle) return;
    const roleIds = this.inviteRoleIdsText
      .split(',')
      .map(value => Number(value.trim()))
      .filter(value => Number.isFinite(value));
    this.hrService.inviteEmployee(this.employeeId, { ...this.inviteForm, roleIds }).subscribe({
      next: response => {
        this.invitationDialogVisible = false;
        this.employee = response.data ?? this.employee;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Employee invitation sent.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to invite employee.' })
    });
  }

  requestTransfer(): void {
    if (!this.canManageLifecycle) return;
    this.hrService.requestTransfer(this.employeeId, this.actionForm).subscribe({
      next: () => {
        this.lifecycleActionDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Transfer request submitted.' });
        this.resetActionForm();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to request transfer.' })
    });
  }

  requestCompensationChange(): void {
    if (!this.canManageLifecycle) return;
    this.hrService.requestCompensationChange(this.employeeId, this.actionForm).subscribe({
      next: () => {
        this.lifecycleActionDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Compensation placeholder submitted.' });
        this.resetActionForm();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to submit compensation placeholder.' })
    });
  }

  suspendEmployee(): void {
    if (!this.canManageLifecycle) return;
    this.hrService.suspendEmployee(this.employeeId, this.actionForm).subscribe({
      next: response => {
        this.lifecycleActionDialogVisible = false;
        this.employee = response.data ?? this.employee;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Employee suspended.' });
        this.resetActionForm();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to suspend employee.' })
    });
  }

  reinstateEmployee(): void {
    if (!this.canManageLifecycle) return;
    this.hrService.reinstateEmployee(this.employeeId, this.actionForm).subscribe({
      next: response => {
        this.lifecycleActionDialogVisible = false;
        this.employee = response.data ?? this.employee;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Employee reinstated.' });
        this.resetActionForm();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to reinstate employee.' })
    });
  }

  requestTermination(): void {
    if (!this.canManageLifecycle) return;
    this.hrService.requestTermination(this.employeeId, this.actionForm).subscribe({
      next: () => {
        this.lifecycleActionDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'HR Lifecycle', detail: 'Termination request submitted.' });
        this.resetActionForm();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Lifecycle', detail: 'Unable to request termination.' })
    });
  }

  completeOffboardingTask(taskId?: number, taskStatus = 'COMPLETED'): void {
    if (!this.canManageOffboarding || !taskId) return;
    this.hrService.completeOffboardingTask(taskId, { taskStatus }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Offboarding', detail: 'Offboarding task updated.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Offboarding', detail: 'Unable to update offboarding task.' })
    });
  }

  completeOffboarding(): void {
    if (!this.canManageOffboarding) return;
    this.hrService.completeOffboarding(this.employeeId, { reason: 'Completed from HR lifecycle workspace.' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Offboarding', detail: 'Offboarding completed.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Offboarding', detail: 'Required offboarding tasks are still open.' })
    });
  }

  canCompleteOffboardingPlan(plan: HrOffboardingPlan): boolean {
    return plan.planStatus !== 'COMPLETED'
      && !!plan.tasks?.length
      && plan.tasks.every(task => !task.requiredTask || this.isTaskClosed(task.taskStatus));
  }

  isTaskClosed(status?: string): boolean {
    return ['COMPLETED', 'WAIVED', 'NOT_REQUIRED', 'CANCELLED'].includes(status || '');
  }

  taskSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'COMPLETED' || status === 'WAIVED' || status === 'NOT_REQUIRED') return 'success';
    if (status === 'OPEN') return 'warn';
    if (status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  planSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'COMPLETED') return 'success';
    if (status === 'IN_PROGRESS') return 'warn';
    if (status === 'OPEN') return 'info';
    if (status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  resetActionForm(): void {
    this.actionForm = {};
  }

  changeStatus(): void {
    if (!this.canChangeStatus) return;
    this.hrService.changeStatus(this.employeeId, this.statusValue, this.statusReason).subscribe({
      next: response => {
        this.statusDialogVisible = false;
        this.employee = response.data ?? this.employee;
        this.statusReason = '';
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Status updated.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to update status.' })
    });
  }

  linkCustomer(): void {
    if (!this.canLinkIdentities) return;
    if (!this.customerId) return;
    this.hrService.linkCustomer(this.employeeId, this.customerId).subscribe({
      next: response => {
        this.customerLinkDialogVisible = false;
        this.employee = response.data ?? this.employee;
        this.customerId = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Portal user linked.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to link portal user.' })
    });
  }

  linkDriver(): void {
    if (!this.canLinkIdentities) return;
    if (!this.driverId) return;
    this.hrService.linkDriver(this.employeeId, this.driverId).subscribe({
      next: response => {
        this.driverLinkDialogVisible = false;
        this.employee = response.data ?? this.employee;
        this.driverId = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Driver linked.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to link driver.' })
    });
  }

  unlink(linkType?: string): void {
    if (!this.canLinkIdentities) return;
    if (!linkType) return;
    this.hrService.unlinkIdentity(this.employeeId, linkType).subscribe({
      next: response => {
        this.employee = response.data ?? this.employee;
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Identity link removed.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to remove identity link.' })
    });
  }

  openStatusDialog(): void {
    if (!this.canChangeStatus) return;
    this.statusValue = this.employee?.status || '';
    this.statusReason = '';
    this.statusDialogVisible = true;
  }

  openCustomerLinkDialog(): void {
    if (!this.canLinkIdentities) return;
    this.customerId = undefined;
    this.customerLinkDialogVisible = true;
  }

  openDriverLinkDialog(): void {
    if (!this.canLinkIdentities) return;
    this.driverId = undefined;
    this.driverLinkDialogVisible = true;
  }

  openLifecycleActionDialog(): void {
    if (!this.canManageLifecycle) return;
    this.resetActionForm();
    this.lifecycleActionDialogVisible = true;
  }

  openOnboardingDialog(): void {
    if (!this.canManageOnboarding) return;
    this.onboardingRequest = {};
    this.onboardingDialogVisible = true;
  }

  openInvitationDialog(): void {
    if (!this.canManageLifecycle) return;
    this.inviteForm = { corporateRole: 'EMPLOYEE' };
    this.inviteRoleIdsText = '';
    this.invitationDialogVisible = true;
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'ONBOARDING') return 'warn';
    if (status === 'SUSPENDED' || status === 'TERMINATED') return 'danger';
    if (status === 'DRAFT') return 'info';
    return 'secondary';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private driverLabel(driver: Driver): string {
    const name = driver.name || driver.legalName || 'Driver';
    const license = driver.licenseNumber ? ` - ${driver.licenseNumber}` : '';
    return `${name} (#${driver.id || '-'})${license}`;
  }

  private customerLabel(customer: Customer): string {
    const name = customer.name || `Customer ${customer.id}`;
    const contact = customer.emailAddress || customer.phoneNumber;
    return contact ? `${name} - ${contact}` : name;
  }

  private isRestrictedDocument(document: HrEmployeeDocument): boolean {
    return (document.confidentialityLevel || '').includes('RESTRICTED');
  }

  private resolveTab(tab: string | null): 'overview' | 'employment' | 'documents' | 'org' | 'links' | 'lifecycle' | 'timeline' {
    return (this.tabKeys.find(value => value === tab) || 'overview') as 'overview' | 'employment' | 'documents' | 'org' | 'links' | 'lifecycle' | 'timeline';
  }
}
